import { memo, PureComponent, useEffect, useMemo } from 'react'

import i18n from 'i18n-literally'
import PropTypes from 'prop-types'
import { pick, trim } from 'ramda'

import { Alert, Box, Fade, LinearProgress } from '@mui/material'
import { styled } from '@mui/material/styles'

import config from '~/src/App/config'
import { TERMS_AND_CONDITIONS_URL } from '~/src/App/constants'
import { getTokens } from '~/src/Lib/Auth'
import createLogger from '~/src/Lib/Logging'
import { addNativeBiometricResumeListener } from '~/src/Native/biometrics'
import { isNativePlatform } from '~/src/Native/platform'
import LoginForm from '~/src/Routes/Login/LoginForm'
import { getLSM } from '~/src/Store/bundles/me'
import { isValidEmail } from '~/src/Store/bundles/me/shape'
import { useConnectModern } from '~/src/Store/hooks/useConnectModern'
import Button from '~/src/UI/Shared/Button'
import PageTitle from '~/src/UI/Shared/PageTitle'
import T from '~/src/UI/Shared/Typography'

const displayName = 'LoginPage'
const logger = createLogger(displayName)

// Emergency switch to disable marketing iframe
const marketingIframeEnabled = config.ENVIRONMENT === 'production'

const Root = styled('section')(({ theme }) => ({
  display: 'grid',
  gridTemplateAreas: '"form"',
  gridTemplateColumns: 'minmax(0, 1fr)',
  height: '100dvh',
  paddingInline: theme.spacing(1),
  '& iframe': {
    border: 'none',
  },
  [theme.breakpoints.up('md')]: marketingIframeEnabled
    ? {
        gridTemplateAreas: '"form marketing"',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
        paddingInline: 0,
      }
    : {},
}))

const FormPane = styled('div')({
  gridArea: 'form',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
})

const Logo = styled('img')({
  marginBottom: '1ch',
  width: '10rem',
})

const FacilityLoadingTitle = styled(PageTitle)(({ theme }) => ({
  boxSizing: 'border-box',
  minWidth: 0,
  overflowWrap: 'anywhere',
  paddingInline: theme.spacing(2),
  textAlign: 'center',
  width: '100%',
}))

const StatusBanner = styled('div')(({ theme }) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  paddingBlockStart: theme.spacing(1),
  zIndex: theme.zIndex.snackbar,
  '& .MuiAlert-message': {
    fontFamily: theme.typography.boldFontFamily,
    fontSize: 14,
    lineHeight: 1.2,
  },
  [theme.breakpoints.up('sm')]: {
    '& .MuiAlert-message': {
      fontSize: 16,
      lineHeight: 1.75,
    },
  },
  [theme.breakpoints.up('md')]: marketingIframeEnabled
    ? {
        width: '50%',
      }
    : {},
}))

const Footer = styled('footer')(({ theme }) => ({
  position: 'fixed',
  bottom: 0,
  left: 0,
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 0,
  paddingBlockEnd: theme.spacing(1),
  textAlign: 'center',
  [theme.breakpoints.up('md')]: marketingIframeEnabled
    ? {
        width: '50%',
        flexDirection: 'row',
        '& .copyright': {
          borderRight: '1px solid currentColor',
          paddingInlineEnd: theme.spacing(1),
        },
        '& .terms': {
          paddingInlineStart: theme.spacing(1),
        },
      }
    : {},
}))

const MarketingFrame = styled('iframe')(({ theme }) => ({
  display: 'none',
  gridArea: 'marketing',
  height: '100%',
  width: '100%',
  [theme.breakpoints.up('md')]: {
    display: 'block',
  },
}))

const bannerMessage = state => {
  if (state.forgot) {
    return i18n`An email was sent with a link to reset your password.`
  }
  return i18n`Your password has been saved. Please login below.`
}

const loginPicker = pick(['email', 'password'])

export class LoginComponent extends PureComponent {
  static displayName = displayName

  static propTypes = {
    auth: PropTypes.shape({
      authenticated: PropTypes.bool,
      loginFailed: PropTypes.bool,
      loginMessage: PropTypes.string,
      loginProcessing: PropTypes.bool,
      biometric: PropTypes.shape({
        availability: PropTypes.oneOf(['unknown', 'available', 'unavailable']),
        enabled: PropTypes.bool,
        label: PropTypes.string,
        processing: PropTypes.bool,
      }),
      forgot: PropTypes.shape({
        success: PropTypes.bool,
      }),
      reset: PropTypes.shape({
        success: PropTypes.bool,
      }),
    }).isRequired,
    queryAdvanced: PropTypes.shape({
      reset: PropTypes.bool,
    }).isRequired,
    switching: PropTypes.bool.isRequired,
    switchingTo: PropTypes.string.isRequired,
    doLogin: PropTypes.func.isRequired,
    doBiometricLogin: PropTypes.func.isRequired,
    doBiometricCheck: PropTypes.func.isRequired,
    doLogout: PropTypes.func.isRequired,
    doForgotClearState: PropTypes.func.isRequired,
    doResetClearState: PropTypes.func.isRequired,
  }

  showLogoutTimeout = null

  constructor(props) {
    super(props)
    this.state = {
      copyYear: new Date().getFullYear(),
      email: '',
      password: '',
      forgot: props.auth.forgot.success,
      reset: props.queryAdvanced?.reset ?? false,
      showLogout: false,
      startLogout: false,
    }
    this.setState = this.setState.bind(this)

    if (this.state.forgot) {
      this.props.doForgotClearState()
    }
  }

  showLogout = () => {
    logger.debug('[timeout-handler] showing logout button')
    this.setState({ showLogout: true })
  }

  componentDidUpdate(_, prevState) {
    const {
      auth: { authenticated },
    } = this.props

    if (authenticated && !this.showLogoutTimeout) {
      logger.debug('seting timeout to show logout button')
      this.showLogoutTimeout = setTimeout(this.showLogout, 10_000)
    }

    if (!prevState.startLogout && this.state.startLogout) {
      setTimeout(() => this.props.doLogout(), 250)
    }
  }

  componentWillUnmount() {
    clearTimeout(this.showLogoutTimeout)
    if (this.showLogoutTimeout) {
      this.showLogoutTimeout = null
    }
  }

  onSubmit = async event => {
    event.preventDefault()
    this.setState({ forgot: undefined, reset: false })
    this.props.doForgotClearState()
    this.props.doResetClearState()
    if (this.isFormValid()) {
      this.props.doLogin(loginPicker(this.state))
    }
  }

  isFormValid() {
    const { email, password } = this.state
    return Boolean(isValidEmail(email) && trim(password).length)
  }

  render() {
    const {
      auth: { authenticated, loginProcessing, loginFailed, loginMessage },
      switching,
      switchingTo,
    } = this.props

    const { copyYear, forgot, reset, showLogout, startLogout } = this.state
    const showBanner = loginFailed || this.state.forgot || this.state.reset
    if (showLogout) {
      logger.debug('[render] showing logout button')
    }
    if (authenticated || startLogout) {
      const pageTitle = switching ? i18n`Switching to ${switchingTo}` : i18n`Welcome back!`
      return (
        <Fade in={!startLogout}>
          <div className="center-all">
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '1ch',
                height: '25vh',
              }}
            >
              <FacilityLoadingTitle disablePortal noWrap={false} title={pageTitle} />
              <T align="center" sx={{ flex: '0 0 100%' }}>
                {i18n`Loading your facility data.`}
              </T>
              <Box
                sx={{
                  flex: '0 0 100%',
                  paddingY: '1ch',
                  paddingX: 'calc(50% - 9rem)',
                }}
              >
                <LinearProgress variant="indeterminate" color="primary" />
              </Box>
              {showLogout ? (
                <Fade in>
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    sx={{ maxHeight: 42 }}
                    onClick={() => {
                      this.setState({ startLogout: true })
                    }}
                  >
                    {i18n`Retry`}
                  </Button>
                </Fade>
              ) : (
                <Box
                  sx={{
                    height: 42,
                  }}
                />
              )}
            </Box>
          </div>
        </Fade>
      )
    }

    return (
      <Root className="aroya__no-auto-height">
        <FormPane>
          <Logo src="/images/aroya-logo-large.svg" alt="AROYA logo" />
          {showBanner ? (
            <StatusBanner>
              {loginFailed && !forgot && !reset ? (
                <Alert data-testid="login-failed-banner" severity="error" variant="outlined">
                  {loginMessage}
                </Alert>
              ) : null}
              {reset || forgot ? (
                <Alert
                  data-testid={`login-${this.state.forgot ? 'forgot' : 'reset'}-banner`}
                  severity="success"
                  variant="outlined"
                >
                  {bannerMessage(this.state)}
                </Alert>
              ) : null}
            </StatusBanner>
          ) : null}
          <LoginForm
            email={this.state.email}
            biometric={this.props.auth.biometric}
            doBiometricLogin={this.props.doBiometricLogin}
            isFormValid={this.isFormValid()}
            loginProcessing={loginProcessing}
            onSubmit={this.onSubmit}
            password={this.state.password}
            setState={this.setState}
          />
          <Footer>
            <T className="copyright">
              &copy; {copyYear} {i18n`Addium Inc. All rights reserved.`}
            </T>
            <T.Bold
              className="terms"
              sx={{ color: 'primary.main' }}
              component="a"
              href={TERMS_AND_CONDITIONS_URL}
              target="_blank"
            >
              {i18n`Terms & Conditions`}
            </T.Bold>
          </Footer>
        </FormPane>
        {marketingIframeEnabled ? (
          <MarketingFrame
            data-testid="aroya__marketing-iframe"
            src="https://addium.marketing/assets/app-banner.html"
            title="marketing"
          />
        ) : null}
      </Root>
    )
  }
}

const Connected = ({ switching = false, ...passthru }) => {
  const connectedProps = useConnectModern(
    'selectAuth',
    'selectQueryAdvanced',
    'doBiometricCheck',
    'doForgotClearState',
    'doBiometricLogin',
    'doLogin',
    'doLogout',
    'doResetClearState',
  )
  const { doBiometricCheck } = connectedProps

  useEffect(() => {
    if (!isNativePlatform()) return undefined
    let active = true
    let removeResumeListener = () => Promise.resolve()
    void doBiometricCheck()
    void addNativeBiometricResumeListener(
      () => {
        if (active) void doBiometricCheck()
      },
      { native: true },
    )
      .then(remove => {
        removeResumeListener = remove
        if (!active) void remove()
      })
      .catch(() => undefined)
    return () => {
      active = false
      void removeResumeListener()
    }
  }, [doBiometricCheck])

  const nextFacilityName = useMemo(() => {
    if (switching) {
      const { facilities } = getTokens()
      const { facility } = getLSM()
      const nextFacility = Array.isArray(facilities) ? facilities.find(f => f.id === facility) : null
      return nextFacility ? nextFacility.name : ''
    }
    return ''
  }, [switching])

  return <LoginComponent {...connectedProps} {...passthru} switching={switching} switchingTo={nextFacilityName} />
}
Connected.displayName = 'LoginPage'

const Memoized = memo(Connected)
export default Memoized
